import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import ExistingPackageList from './ExistingPackageList';
import NewPackageForm from './NewPackageForm';
import { fetchRoomTypes, fetchPackageList } from '../../../service/network';
import { useLoading } from "../../../context/loading.context";
import theme from "../../../assets/theme/index"
const initialPackageData = {
    packageName: "",
    packageCode: "",
    status: "Active",
    roomType: "",
    fromDate: "",
    toDate: "",
    selectedDays: [],
    baseRate: "",
    adults: "",
    children: "",
    extraAdultCharge: "",
    extraChildCharge: "",
    weekendSurcharge: "",
    surchargeType: "$",
    selectedWeekends: [],
    hasDetails: false,
    isExpanded: false,
    isEditing: false,
    packageDetails: []
};

export default function TariffRow({ showNewPackageForm, setShowNewPackageForm, filters }) {
    const [allPackages, setAllPackages] = useState([]);
    const [filteredPackages, setFilteredPackages] = useState([]);
    const [newPackage, setNewPackage] = useState({ ...initialPackageData, isEditing: true });
    const [roomTypes, setRoomTypes] = useState([{ id: 1 }]);
    const [packageOptions, setPackageOptions] = useState([]);
    const [roomTypeOptions, setRoomTypeOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [savingStates, setSavingStates] = useState({});
    const { showLoader, hideLoader } = useLoading();

    const [dataLoaded, setDataLoaded] = useState(false);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                showLoader();
                // Fetch room types
                const roomTypeParams = {
                    doctype: 'Room Type',
                    fields: JSON.stringify(["name", "room_type"])
                };
                const roomTypeRes = await fetchRoomTypes(roomTypeParams);
                const roomTypes = roomTypeRes.data.message.values.map(item => item[1]);
                setRoomTypeOptions([...new Set(roomTypes)]);

                // Load packages
                await loadPackages();
                setDataLoaded(true); // Mark data as loaded
            } catch (err) {
                console.error("Failed to fetch initial data", err);
                setDataLoaded(true); // Even if error, data loading attempt is complete
            } finally {
                hideLoader();
                setIsLoading(false);
            }
        };

        loadInitialData();
    }, []);

    useEffect(() => {
        // Apply filters whenever filters or allPackages change
        if (allPackages.length > 0) {
            const filtered = allPackages.filter(pkg => {
                // Filter by room type
                if (filters.roomType && !pkg.packageDetails.some(detail =>
                    detail.roomType === filters.roomType)) {
                    return false;
                }

                // Filter by status
                if (filters.status && pkg.status !== filters.status) {
                    return false;
                }

                // Filter by search query (name or code)
                if (filters.searchQuery) {
                    const searchLower = filters.searchQuery.toLowerCase();
                    if (!pkg.packageName.toLowerCase().includes(searchLower) &&
                        !pkg.packageCode.toLowerCase().includes(searchLower)) {
                        return false;
                    }
                }

                return true;
            });
            setFilteredPackages(filtered);
        }
    }, [filters, allPackages]);

    const loadPackages = async () => {
        try {
            showLoader();
            // Fetch package data
            const packageParams = {
                doctype: 'Room Package',
                fields: JSON.stringify(["package_name", "package_code", "status", "name"])
            };
            const packageRes = await fetchPackageList(packageParams);
            const packageData = packageRes.data.message.values.map((item, index) => ({
                ...initialPackageData,
                name: item[3],
                packageName: item[0],
                packageCode: item[1],
                status: item[2],
                id: index + 1,
                packageDetails: [] // Initialize empty details array
            }));

            setPackageOptions(packageRes.data.message.values.map(item => ({
                packageName: item[0],
                packageCode: item[1],
                status: item[2],
            })));

            setAllPackages(packageData.length > 0 ? packageData : [{
                ...initialPackageData,
                id: 1,
                packageName: "Standard Package",
                packageCode: "STD-003"
            }]);
        } catch (err) {
            console.error("Failed to fetch packages", err);
        } finally {
            hideLoader();
        }
    };

    const toggleDay = (day, packageId, detailIndex = null) => {
        if (packageId) {
            setAllPackages(allPackages.map(pkg => {
                if (pkg.id !== packageId) return pkg;

                if (detailIndex !== null) {
                    // Update specific package detail
                    const updatedDetails = [...pkg.packageDetails];
                    updatedDetails[detailIndex] = {
                        ...updatedDetails[detailIndex],
                        selectedDays: updatedDetails[detailIndex].selectedDays.includes(day)
                            ? updatedDetails[detailIndex].selectedDays.filter(d => d !== day)
                            : [...updatedDetails[detailIndex].selectedDays, day]
                    };
                    return { ...pkg, packageDetails: updatedDetails };
                } else {
                    // Update main package
                    return {
                        ...pkg,
                        selectedDays: pkg.selectedDays.includes(day)
                            ? pkg.selectedDays.filter(d => d !== day)
                            : [...pkg.selectedDays, day]
                    };
                }
            }));
        } else {
            setNewPackage({
                ...newPackage,
                selectedDays: newPackage.selectedDays.includes(day)
                    ? newPackage.selectedDays.filter(d => d !== day)
                    : [...newPackage.selectedDays, day]
            });
        }
    };

    const toggleWeekend = (day, packageId, detailIndex = null) => {
        if (packageId) {
            setAllPackages(allPackages.map(pkg => {
                if (pkg.id !== packageId) return pkg;

                if (detailIndex !== null) {
                    // Update specific package detail
                    const updatedDetails = [...pkg.packageDetails];
                    updatedDetails[detailIndex] = {
                        ...updatedDetails[detailIndex],
                        selectedWeekends: updatedDetails[detailIndex].selectedWeekends.includes(day)
                            ? updatedDetails[detailIndex].selectedWeekends.filter(d => d !== day)
                            : [...updatedDetails[detailIndex].selectedWeekends, day]
                    };
                    return { ...pkg, packageDetails: updatedDetails };
                } else {
                    // Update main package
                    return {
                        ...pkg,
                        selectedWeekends: pkg.selectedWeekends.includes(day)
                            ? pkg.selectedWeekends.filter(d => d !== day)
                            : [...pkg.selectedWeekends, day]
                    };
                }
            }));
        } else {
            setNewPackage({
                ...newPackage,
                selectedWeekends: newPackage.selectedWeekends.includes(day)
                    ? newPackage.selectedWeekends.filter(d => d !== day)
                    : [...newPackage.selectedWeekends, day]
            });
        }
    };

    const toggleAllDays = (packageId, detailIndex = null) => {
        const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        if (packageId) {
            setAllPackages(allPackages.map(pkg => {
                if (pkg.id !== packageId) return pkg;

                if (detailIndex !== null) {
                    // Update specific package detail
                    const updatedDetails = [...pkg.packageDetails];
                    updatedDetails[detailIndex] = {
                        ...updatedDetails[detailIndex],
                        selectedDays: updatedDetails[detailIndex].selectedDays.length === 7 ? [] : [...daysOfWeek]
                    };
                    return { ...pkg, packageDetails: updatedDetails };
                } else {
                    // Update main package
                    return {
                        ...pkg,
                        selectedDays: pkg.selectedDays.length === 7 ? [] : [...daysOfWeek]
                    };
                }
            }));
        } else {
            setNewPackage({
                ...newPackage,
                selectedDays: newPackage.selectedDays.length === 7 ? [] : [...daysOfWeek]
            });
        }
    };

    const handlePackageChange = (field, value, packageId, detailIndex = null) => {
        if (packageId) {
            setAllPackages(allPackages.map(pkg => {
                if (pkg.id !== packageId) return pkg;

                if (detailIndex !== null) {
                    // Update specific package detail
                    const updatedDetails = [...pkg.packageDetails];
                    updatedDetails[detailIndex] = {
                        ...updatedDetails[detailIndex],
                        [field]: value
                    };
                    return { ...pkg, packageDetails: updatedDetails };
                } else {
                    // Update main package
                    return { ...pkg, [field]: value };
                }
            }));
        } else {
            setNewPackage({ ...newPackage, [field]: value });
        }
    };

    const handleEdit = (packageId) => {
        setAllPackages(allPackages.map(pkg =>
            pkg.id === packageId ? { ...pkg, isEditing: !pkg.isEditing } : pkg
        ));
    };
    const handleCopy = (packageId) => {
        const packageToCopy = allPackages.find(pkg => pkg.id === packageId);

        if (!packageToCopy) return;

        if (!packageToCopy.isExpanded) {
            showNotification('warning', 'Action Blocked', 'Please expand the package before copying.');
            return;
        }

        const newId = Math.max(...allPackages.map(pkg => pkg.id), 0) + 1;
        const baseName = `${packageToCopy.packageName} - Copy`;
        const copyNumber = allPackages.filter(pkg =>
            pkg.packageName.startsWith(baseName)
        ).length + 1;

        const newPackage = {
            ...packageToCopy,
            id: newId,
            isEditing: true,
            isExpanded: true, // optionally auto-expand the copied package
            packageName: `${baseName} ${copyNumber}`,
            packageCode: `${packageToCopy.packageCode}-CP${copyNumber}`
        };

        setAllPackages([...allPackages, newPackage]);
    };


    const handleSave = async (packageId) => {
        const pkg = allPackages.find(p => p.id === packageId);
        if (!pkg) return;

        setSavingStates(prev => ({ ...prev, [packageId]: true }));

        try {
            // Validate required fields
            if (!pkg.packageName || !pkg.packageCode) {
                throw new Error('Package name and code are required');
            }

            // Update package fields (title, code, status) only
            await updatePackage(pkg.name, {
                package_name: pkg.packageName,
                package_code: pkg.packageCode,
                status: pkg.status
            });

            // Update all package details
            for (const detail of pkg.packageDetails) {
                await updatePackageDetails(detail.name, {
                    room_type: detail.roomType,
                    from_date: detail.fromDate,
                    to_date: detail.toDate,
                    applicable_days: JSON.stringify(detail.selectedDays.map(day => day.toLowerCase())),
                    rate_per_night: detail.baseRate,
                    no_of_adult: detail.adults,
                    no_of_child: detail.children,
                    extra_adult_charge: detail.extraAdultCharge,
                    extra_child_charge: detail.extraChildCharge,
                    surcharge: detail.weekendSurcharge,
                    surcharge_method: detail.surchargeType,
                    weekend_days: JSON.stringify(detail.selectedWeekends.map(day => day.toLowerCase()))
                });
            }

            setAllPackages(prevPackages =>
                prevPackages.map(item =>
                    item.id === packageId ? { ...item, isEditing: false } : item
                )
            );

            showNotification('success', 'Success', 'Package updated successfully.');
        } catch (error) {
            console.error('Error updating package:', error);
            let errorMessage = error.message || 'Failed to update package.';

            if (error.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(error.response.data._server_messages)
                        .map(msg => JSON.parse(msg).message.replace(/<[^>]+>/g, ''))
                        .join(', ');
                    errorMessage = messages;
                } catch (parseErr) {
                    console.error('Error parsing server messages:', parseErr);
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            showNotification('error', 'Error', errorMessage);
        } finally {
            setSavingStates(prev => ({ ...prev, [packageId]: false }));
        }
    };

    const handleSaveNewPackage = async () => {
        try {
            // Save new package logic here
            await loadPackages(); // Refresh the package list after save
            setNewPackage({ ...initialPackageData, isEditing: true });
            setShowNewPackageForm(false);
        } catch (error) {
            console.error("Error saving new package:", error);
        }
    };

    const handleDelete = async (packageId) => {
        try {
            // Delete logic here
            await loadPackages(); // Refresh the package list after delete
        } catch (error) {
            console.error("Error deleting package:", error);
        }
    };

    const addRoomType = () => {
        setRoomTypes([...roomTypes, { id: roomTypes.length + 1 }]);
    };

    const handleToggleExpand = (id) => {
        setAllPackages(allPackages.map(pkg =>
            pkg.id === id ? { ...pkg, isExpanded: !pkg.isExpanded } : pkg
        ));
    };
    const handleClose = (packageId) => {
  setAllPackages(allPackages.map(pkg => 
    pkg.id === packageId ? { ...pkg, isExpanded: false, isEditing: false } : pkg
  ));
};

    const updatePackage = async (packageName, data) => {
        // Implement your API call to update package
        console.log("Updating package:", packageName, data);
        // Example:
        // return await api.updatePackage(packageName, data);
    };

    const updatePackageDetails = async (detailName, data) => {
        // Implement your API call to update package details
        console.log("Updating package details:", detailName, data);
        // Example:
        // return await api.updatePackageDetails(detailName, data);
    };

    const showNotification = (type, title, message) => {
        // Implement your notification system
        console.log(`[${type}] ${title}: ${message}`);
    };

    return (
        <Box>
            {showNewPackageForm && (
                <NewPackageForm
                    newPackage={newPackage}
                    handlePackageChange={handlePackageChange}
                    toggleDay={toggleDay}
                    toggleWeekend={toggleWeekend}
                    toggleAllDays={toggleAllDays}
                    handleSaveNewPackage={handleSaveNewPackage}
                    addRoomType={addRoomType}
                    roomTypes={roomTypes}
                    setRoomTypes={setRoomTypes}
                    setShowNewPackageForm={setShowNewPackageForm}

                    roomTypeOptions={roomTypeOptions}
                    onSuccess={() => {
                        setShowNewPackageForm(false);
                        loadPackages();
                    }}
                />
            )}
            {dataLoaded && filteredPackages.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: theme.borderRadius.medium,
                    boxShadow: theme.shadows[1],
                    mt: 2
                }}>
                    <Typography variant="h6" color="textSecondary">
                        {filters.roomType || filters.status || filters.searchQuery
                            ? "No packages match your filters"
                            : "No packages available"}
                    </Typography>
                </Box>
            ) : (
                <ExistingPackageList
                    packages={filteredPackages}
                    setPackages={setAllPackages}
                    packageOptions={packageOptions}
                    roomTypeOptions={roomTypeOptions}
                    handleEdit={handleEdit}
                    handleCopy={handleCopy}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    handleClose={handleClose}  // Changed from handleDelete

                    handlePackageChange={handlePackageChange}
                    toggleDay={toggleDay}
                    toggleWeekend={toggleWeekend}
                    toggleAllDays={toggleAllDays}
                    handleToggleExpand={handleToggleExpand}
                />
            )}


        </Box>
    );
}